/* eslint-disable complexity */
import type { Incident } from '@/lib/queries/rca/incidents';

import { getPrimaryIncidentData } from '../-components/IncidentCardHelpers';

const priorityOrder = { P1: 0, P2: 1, P3: 2, P4: 3, P5: 4 };
const statusOrder = { triggered: 0, acknowledged: 1, resolved: 2 };

const getPriorityOrder = (priority?: string): number => {
  if (priority === undefined) {
    return 999; // Undefined priorities go to the end
  }

  if (priority in priorityOrder) {
    return priorityOrder[priority as keyof typeof priorityOrder];
  }

  // Custom priorities go after P5 but before undefined
  return 100;
};

const getStatusOrder = (status: string): number => {
  if (!(status in statusOrder)) {
    return 0;
  }

  return statusOrder[status as keyof typeof statusOrder];
};

export const sortIncidents = (
  incidents: Incident[],
  sortBy: string,
): Incident[] => {
  return [...incidents].sort((a, b) => {
    if (!a.pagerDutyIncident || !b.pagerDutyIncident) {
      return 0;
    }

    switch (sortBy) {
      case 'newest':
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      case 'oldest':
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      case 'incidentNumber': {
        const aData = getPrimaryIncidentData(a);
        const bData = getPrimaryIncidentData(b);

        return (aData?.incidentNumber ?? 0) - (bData?.incidentNumber ?? 0);
      }
      case 'severity': {
        const aData = getPrimaryIncidentData(a);
        const bData = getPrimaryIncidentData(b);

        return (
          getPriorityOrder(aData?.priority) - getPriorityOrder(bData?.priority)
        );
      }
      case 'status': {
        const aData = getPrimaryIncidentData(a);
        const bData = getPrimaryIncidentData(b);

        return (
          getStatusOrder(aData?.sourceStatus ?? '') -
          getStatusOrder(bData?.sourceStatus ?? '')
        );
      }
      default:
        return 0;
    }
  });
};
